import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import "../All CSS/LoginForm.css";
import { FaEye, FaEyeSlash, FaUser, FaLock } from "react-icons/fa";
import { useAuth } from "../Components/StoredDetails";
import { useWindowContext } from "../Context/WindowContext";
import ERP from '../All images/ERP.jpg';
import Logo from '../All images/Logo_MBC.jpg';
// import Logo from '../All images/EEC-Logo.png';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const LoginForm = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const { email: routeEmail, password: routePassword } = useParams();

  // Forgot Password
  const [forgotStep, setForgotStep] = useState(0);
  const [otp, setOtp] = useState("");
  const [enteredOtp, setEnteredOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Location Popup
  const {
    authData,
    setUserData,
    setLocations,
    setSelectedLocation
  } = useAuth();
  const { clearWindows } = useWindowContext();
  const [locationPopup, setLocationPopup] = useState(false);
  const [showLocationTable, setShowLocationTable] = useState(false);
  const locations = authData.locations;
  const [selectedLocation, setLocalSelectedLocation] = useState(null);

  // Alert Popup
  const [alertPopup, setAlertPopup] = useState({
    visible: false,
    message: "",
    type: "error",
  });

  const showAlert = useCallback((message, type = "error") => {
    setAlertPopup({ visible: true, message, type });
  }, []);

  const closeAlert = useCallback(() => {
    setAlertPopup({ visible: false, message: "", type: "error" });
  }, []);

  const handleLogin = useCallback(
    async (mail = email, pwd = password) => {
      if (!mail || !pwd) {
        showAlert("Please enter UserID and Password");
        return;
      }

      try {

        const response = await axios.post(
          `${API_BASE_URL}/api/login`,
          {
            email: mail,
            password: pwd,
          }
        );

        const data = response.data;

        if (
          data?.success &&
          String(data?.RespCode) === "0"
        ) {
          // Remember Me
          if (rememberMe) {
            localStorage.setItem(
              "rememberedCredentials",
              JSON.stringify({
                email: mail,
                password: pwd,
              })
            );
          } else {
            localStorage.removeItem(
              "rememberedCredentials"
            );
          }

          // User Details
          const userDetails = {
            ...(data.UserDetails || {}),
            UserType: "User",
          };

          setUserData(userDetails);

          // Locations
          const userLocations = data.Locations || [];

          setLocations(userLocations);

          // No location
          if (userLocations.length === 0) {
            showAlert(
              "No location assigned to this user."
            );
            return;
          }

          // Find default location
          const defaultLocation =
            userLocations.find(
              (loc) =>
                String(
                  loc.DefaultLocation
                ).toUpperCase() === "Y"
            );

          // Default location found
          if (defaultLocation) {
            setSelectedLocation(
              defaultLocation
            );

            navigate("/home");

            return;
          }

          // No default location
          setLocalSelectedLocation(null);
          setShowLocationTable(false);
          setLocationPopup(true);
        } else {
          showAlert(
            data?.RespMessage ||
            "Invalid login credentials"
          );
        }
      } catch (err) {
        console.error(
          "Login Error:",
          err.response?.data ||
          err.message ||
          err
        );

        showAlert(
          err.response?.data?.RespMessage ||
          "Unable to login. Please try again."
        );
      }
    },
    [
      email,
      password,
      rememberMe,
      setUserData,
      setLocations,
      setSelectedLocation,
      navigate,
      showAlert,
    ]
  );

  useEffect(() => {
    if (routeEmail && routePassword) {
      setEmail(routeEmail);
      setPassword(routePassword);
      handleLogin(routeEmail, routePassword);
    }
  }, [routeEmail, routePassword, handleLogin]);

  useEffect(() => {
    clearWindows();
  }, [clearWindows]);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleLogin();
  };

  const togglePasswordVisibility = () => {
    setShowPassword(
      (prev) => !prev
    );
  };

  const handleSendOtp = async () => {
    try {
      const res = await axios.post(`${API_BASE_URL}/api/forgot-password-otp`, {
        userId: email,
      });

      if (res.data.RespCode === "0" || res.data.RespCode === 0) {
        setOtp(res.data.RespMessage);
        setForgotStep(2);
        showAlert("OTP sent successfully!", "success");
      } else {
        showAlert(
          res.data.RespMessage || "Something went wrong. Please try again.",
        );
      }
    } catch (err) {
      showAlert("Failed to send OTP. Please check your connection.");
    }
  };

  const handleConfirmOtp = () => {
    if (enteredOtp.trim() === "") {
      showAlert("Please enter the OTP.");
      return;
    }
    if (enteredOtp === otp) {
      setForgotStep(3);
      showAlert("OTP verified successfully!", "success");
    } else {
      showAlert("OTP does not match. Please try again.");
    }
  };

  const handleResetPassword = async () => {
    if (newPassword !== confirmPassword) {
      showAlert("Passwords do not match!");
      return;
    }
    try {
      const res = await axios.post(`${API_BASE_URL}/api/reset-password`, {
        userId: email,
        newPassword,
      });

      if (res.data.RespCode === "0") {
        setForgotStep(0);
        setPassword("");
        setNewPassword("");
        setConfirmPassword("");
        showAlert(
          "Password reset successfully! You can now log in with your new password.",
          "success",
        );
      } else {
        showAlert(res.data.RespMessage);
      }
    } catch {
      showAlert("Failed to reset password.");
    }
  };

  useEffect(() => {

    try {

      const remembered =
        localStorage.getItem(
          "rememberedCredentials"
        );

      if (remembered) {

        const credentials =
          JSON.parse(remembered);

        setEmail(
          credentials?.email || ""
        );

        setPassword(
          credentials?.password || ""
        );

        setRememberMe(true);
      }

    } catch (error) {

      console.error(
        "Error reading remembered credentials:",
        error
      );

      localStorage.removeItem(
        "rememberedCredentials"
      );
    }

  }, []);

  return (
    <div className="Login-form-page">
      {/* Right Side Logo */}
      <div className="Login-from-Right">
        <img src={ERP} alt="ERP Logo" className="ERP-Logo" />
      </div>

      <div className="Login-from-Left">
        <div className="left-side-logo">
          <img
            src={Logo}
            alt="ERP Logo"
            className="IDDS-Logo"
          />
        </div>

        {/* Login Form */}
        {forgotStep === 0 && (
          <div className="Login-form-container">
            <form
              onSubmit={handleFormSubmit}
              className="Login-form"
            >
              <h2 className="Login-form-heading">Login</h2>
              <div className="Login-form-group">
                <span className="input-icon">
                  <FaUser />
                </span>
                <input
                  type="text"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder=" "
                  required
                  className="Login-form-group-input with-icon"
                />
                <label htmlFor="email" className="Login-form-group-label">
                  Enter UserID / EmailID / Mobile
                </label>
              </div>
              <div className="Login-form-group">
                <span className="input-icon">
                  <FaLock />
                </span>
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder=" "
                  required
                  className="Login-form-group-input with-icon"
                />
                <label htmlFor="password" className="Login-form-group-label">
                  Password
                </label>
                <span
                  className="password-toggle-icon"
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>
              <div className="Login-form-options">
                <div className="remember-me">
                  <input
                    type="checkbox"
                    id="rememberMe"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <label htmlFor="rememberMe">Remember Me</label>
                </div>
                <button
                  type="button"
                  onClick={() => setForgotStep(1)}
                  className="forgot-password-link"
                >
                  Forgot Password?
                </button>
              </div>
              <div className="Login-form-footer">
                <button type="submit" className="Login-form-submit-button">
                  Login
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Step 1: Send OTP */}
        {forgotStep === 1 && (
          <div className="Login-form-container small-box">
            <div className="Login-form-heading-container">
              <button
                className="Back-Button"
                onClick={() => setForgotStep(forgotStep - 1)}
              >
                ← Back
              </button>
              <h2 className="Login-heading">Reset Password</h2>
            </div>
            <div className="Login-form-group">
              <input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder=""
                className="Login-form-group-input"
              />
              <label htmlFor="userId" className="Login-form-group-label">
                Login ID
              </label>
            </div>
            <button
              onClick={handleSendOtp}
              className="Login-form-submit-button"
            >
              Send OTP
            </button>
          </div>
        )}

        {/* Step 2: Confirm OTP */}
        {forgotStep === 2 && (
          <div className="Login-form-container small-box">
            <div className="Login-form-group">
              <input
                type="text"
                value={enteredOtp}
                onChange={(e) => setEnteredOtp(e.target.value)}
                placeholder=""
                className="Login-form-group-input"
              />
              <label htmlFor="OTP" className="Login-form-group-label">
                Enter OTP
              </label>
            </div>
            <button
              onClick={handleConfirmOtp}
              className="Login-form-submit-button"
            >
              Confirm
            </button>
          </div>
        )}

        {/* Step 3: Reset Password */}
        {forgotStep === 3 && (
          <div className="Login-form-container small-box">
            <div className="Login-form-group">
              <input
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder=""
                className="Login-form-group-input"
              />
              <label htmlFor="New Password" className="Login-form-group-label">
                New Password
              </label>
              <span
                onClick={togglePasswordVisibility}
                className="password-toggle-icon"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
            <div className="Login-form-group">
              <input
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder=""
                className="Login-form-group-input"
              />
              <label
                htmlFor="Confirm Password"
                className="Login-form-group-label"
              >
                Confirm Password
              </label>
              <span
                onClick={togglePasswordVisibility}
                className="password-toggle-icon"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
            <button
              onClick={handleResetPassword}
              className="Login-form-submit-button"
            >
              Reset Password
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="Logo-container">
          <p className="Logo-Container-text">
            <span className="logo-sfactor">SFactor</span>
            <span className="logo-erp">ERP </span>
            <span className="logo-version">
              <a
                href="https://sfactorerp.com/version/3.0"
                target="_blank"
                rel="noopener noreferrer"
              >
                1.0.2.0
              </a>
            </span>
          </p>
        </div>

        <div className="Company-Link">
          <a
            href="https://superinfolabs.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Powered By Super Infolabs Pvt. Ltd.
          </a>
        </div>
      </div>

      {/* Location Popup */}
      {locationPopup && (
        <div className="Location-popup-overlay">
          <div className="Location-popup-content">
            <h3>Select Location</h3>
            <div className="Location-select-container">
              <input
                type="text"
                readOnly
                value={selectedLocation?.CompLocation || ""}
                placeholder="Click to select location"
                onClick={() => setShowLocationTable(true)}
                className="Location-input"
              />
              {showLocationTable && (
                <div className="Location-table-container">
                  <table className="Location-table">
                    <thead>
                      <tr>
                        <th>Location</th>
                        <th>Code</th>
                      </tr>
                    </thead>
                    <tbody>
                      {locations.map((loc, index) => (
                        <tr
                          key={index}
                          onClick={() => {
                            setLocalSelectedLocation(loc); // ✅ local state only
                            setShowLocationTable(false);
                          }}
                          className="Location-row"
                        >
                          <td>{loc.CompLocation}</td>
                          <td>{loc.CompCode}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="Location-popup-buttons">
              <button
                onClick={() => {
                  if (!selectedLocation) {
                    showAlert("Please select a location");
                    return;
                  }

                  setSelectedLocation(selectedLocation);
                  setLocationPopup(false);
                  navigate("/home");
                }}
                className="Location-popup-Proceed-button"
              >
                Proceed
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Popup */}
      {alertPopup.visible && (
        <div className="Alert-popup-overlay">
          <div className={`Alert-popup-box ${alertPopup.type}`}>
            <p>{alertPopup.message}</p>
            <button onClick={closeAlert} className="Alert-popup-close-btn">
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginForm;
